import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faBookReader,
  faPlus,
  faTimes,
  faDownload,
  faExternalLinkAlt,
  faTrash,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { authFetch, formatDate } from '../utils/http';

interface LibraryItem {
  id: string;
  title: string;
  description: string;
  category: string;
  fileUrl: string | null;
  externalUrl: string | null;
  fileExtension: string | null;
  fileSizeBytes: number;
  learnershipId: string | null;
  learnershipTitle: string | null;
  uploadedAt: string;
  canDelete: boolean;
}

interface CourseRef {
  learnershipId: string;
  title: string;
}

const CATEGORY_SUGGESTIONS = ['Study guide', 'Textbook', 'Past paper', 'Video', 'Template', 'Reference', 'Other'];

const formatSize = (bytes: number) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/**
 * Digital library: supplementary learning material. Learners, moderators browse;
 * trainers and admins can add files or links.
 */
export default function LibraryPage() {
  const { accessToken, user } = useAuth();
  const canAdd = user?.role === 1 || user?.role === 2;
  const isAdmin = user?.role === 2;

  const [items, setItems] = useState<LibraryItem[]>([]);
  const [courses, setCourses] = useState<CourseRef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [itemCategory, setItemCategory] = useState(CATEGORY_SUGGESTIONS[0]);
  const [learnershipId, setLearnershipId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const [itemsRes, coursesRes] = await Promise.all([
      authFetch<LibraryItem[]>(accessToken, '/api/library'),
      canAdd ? authFetch<CourseRef[]>(accessToken, '/api/community/my-courses') : Promise.resolve(null),
    ]);
    if (itemsRes.ok && itemsRes.data) {
      setItems(itemsRes.data);
      setError('');
    } else {
      setError(itemsRes.message || 'Failed to load the library.');
    }
    if (coursesRes && coursesRes.ok && coursesRes.data) setCourses(coursesRes.data);
    setIsLoading(false);
  }, [accessToken, canAdd]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => ['all', ...Array.from(new Set(items.map((i) => i.category)))], [items]);

  const filtered = items.filter((i) => {
    if (category !== 'all' && i.category !== category) return false;
    const q = search.trim().toLowerCase();
    return !q || i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
  });

  const resetForm = () => {
    setShowForm(false);
    setTitle('');
    setDescription('');
    setItemCategory(CATEGORY_SUGGESTIONS[0]);
    setLearnershipId('');
    setExternalUrl('');
    setFile(null);
    setFormError('');
  };

  const save = async () => {
    setFormError('');
    if (!title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!file && !externalUrl.trim()) {
      setFormError('Upload a file or provide a link.');
      return;
    }
    if (!isAdmin && !learnershipId) {
      setFormError('Select a course for this item.');
      return;
    }

    const form = new FormData();
    form.append('title', title);
    form.append('description', description);
    form.append('category', itemCategory);
    if (externalUrl.trim()) form.append('externalUrl', externalUrl.trim());
    if (learnershipId) form.append('learnershipId', learnershipId);
    if (file) form.append('file', file);

    setIsSaving(true);
    const res = await authFetch(accessToken, '/api/library', { method: 'POST', form });
    setIsSaving(false);
    if (!res.ok) {
      setFormError(res.message);
      return;
    }
    resetForm();
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this library item?')) return;
    const res = await authFetch(accessToken, `/api/library/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Digital Library</h1>
          <p className="text-sm text-brand-900/60 mt-1">Additional learning material and academic support</p>
        </div>
        {canAdd && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            Add item
          </button>
        )}
      </div>

      <div className="p-8 space-y-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="h-3.5 w-3.5 text-brand-900/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the library"
              className="w-full pl-9 pr-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c === 'all' ? 'All categories' : c}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faBookReader} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">No library items found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((i) => {
              const href = i.fileUrl ?? i.externalUrl ?? '#';
              return (
                <div key={i.id} className="card-surface p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <span className="px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase">{i.category}</span>
                    {i.canDelete && (
                      <button onClick={() => remove(i.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <h3 className="font-bold text-brand-950 mt-3">{i.title}</h3>
                  {i.description && <p className="text-sm text-brand-900/70 mt-1 flex-1">{i.description}</p>}
                  <p className="text-xs text-brand-900/40 mt-3">
                    {i.learnershipTitle ?? 'All learnerships'} · {formatDate(i.uploadedAt)}
                    {i.fileUrl && ` · ${(i.fileExtension ?? '').toUpperCase()} ${formatSize(i.fileSizeBytes)}`}
                  </p>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={i.fileUrl ? true : undefined}
                    className="mt-4 px-4 py-2 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={i.fileUrl ? faDownload : faExternalLinkAlt} className="h-3 w-3" />
                    {i.fileUrl ? 'Download' : 'Open link'}
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">Add library item</h3>
              <button onClick={resetForm} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Short description"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select
                  value={itemCategory}
                  onChange={(e) => setItemCategory(e.target.value)}
                  className="px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {CATEGORY_SUGGESTIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={learnershipId}
                  onChange={(e) => setLearnershipId(e.target.value)}
                  className="px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {isAdmin && <option value="">All learnerships</option>}
                  {!isAdmin && <option value="">Select course</option>}
                  {courses.map((c) => (
                    <option key={c.learnershipId} value={c.learnershipId}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">File</label>
                <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
                <p className="text-xs text-brand-900/50 mt-1">Max 25 MB. Or provide a link below instead.</p>
              </div>
              <input
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="External link (https://...)"
                className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              {formError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{formError}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={resetForm} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={isSaving}
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                {isSaving ? 'Saving...' : 'Add item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
